/**
 * CE-2 — "Attach job files" picker for the Approve & Send modal.
 *
 * Reintroduces office ad-hoc attachment selection on customer sends (the H15
 * upload affordance was removed because selected files were never actually
 * emailed). This picker lists the job's attachments and lets the office
 * EXPLICITLY check files to ride on the customer email:
 *
 *   · checkboxes default UNCHECKED — nothing rides without a deliberate click;
 *   · an explicit selection overrides category defaults, so internal
 *     'document' / 'summary' rows CAN be sent — they are labeled with an
 *     "internal doc" chip so the choice is informed;
 *   · auto-included categories (reports / photos / filled work orders) are
 *     shown separately as already-riding and cannot be toggled here — the
 *     document checkboxes above the picker control those;
 *   · signature captures never appear (the /api/attachments list excludes
 *     them server-side) and the server refuses to attach them regardless.
 *
 * Selection feeds the existing CE-1 wiring: the ids flow into the send-preview
 * query (drop warnings before the send) and the approve-and-send payload
 * (409 DROPPED_ATTACHMENTS_UNCONFIRMED backstop).
 */
import { useQuery } from "@tanstack/react-query";
import { Loader2, Paperclip } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

export interface JobFileRow {
    id: string;
    file_name: string;
    category: string;
    file_size: number | string | null;
}

/** Categories that already ride via the document checkboxes (reports/photos)
 *  — mirrors the server's REPORT_SENDABLE_CATEGORIES + the photo bundle. */
export const AUTO_RIDE_CATEGORIES = new Set([
    "report",
    "test_report",
    "test_report_official",
    "work_order_filled",
    "photo",
]);

/** Categories that are internal by default — sendable ONLY behind an explicit
 *  selection here, so they get the amber "internal doc" chip. */
export const INTERNAL_DOC_CATEGORIES = new Set(["document", "summary"]);

export function categoryChipLabel(category: string): string {
    if (INTERNAL_DOC_CATEGORIES.has(category)) return "internal doc";
    return category.replace(/_/g, " ");
}

export function formatFileSize(size: number | string | null): string {
    const n = Number(size ?? 0);
    if (!Number.isFinite(n) || n <= 0) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Auto-riding report rows the office may UNCHECK (bulk-group deselect,
 *  2026-07-16). Photos keep their own cap/gallery logic and the merged
 *  city-filing PDF (`report`) no longer rides customer emails at all, so
 *  neither is toggleable. */
export const DESELECTABLE_AUTO_RIDE_CATEGORIES = new Set(["test_report", "test_report_official", "work_order_filled"]);

export function AttachJobFilesPicker({
    jobId,
    selectedIds,
    onChange,
    excludedIds = [],
    onExcludedChange,
}: {
    jobId: string;
    /** Currently checked attachment ids (controlled). */
    selectedIds: string[];
    /** Called with the full new id list on every toggle. */
    onChange: (ids: string[]) => void;
    /** Auto-riding attachment ids the office UNCHECKED — left home on send. */
    excludedIds?: string[];
    /** Enables deselecting auto-riding report files; absent = legacy read-only list. */
    onExcludedChange?: (ids: string[]) => void;
}) {
    const files = useQuery({
        queryKey: ["job-files-picker", jobId],
        queryFn: () => api.get<{ attachments: JobFileRow[] }>(`/api/attachments?jobId=${jobId}&limit=200`),
    });

    const rows = files.data?.attachments ?? [];
    const selectable = rows.filter((r) => !AUTO_RIDE_CATEGORIES.has(r.category));
    const autoRiding = rows.filter((r) => AUTO_RIDE_CATEGORIES.has(r.category));

    if (files.isLoading) {
        return (
            <div className="flex items-center gap-2 text-xs text-foreground/60">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading job files…
            </div>
        );
    }
    if (rows.length === 0) return null;

    const toggle = (id: string, checked: boolean) => {
        onChange(checked ? [...new Set([...selectedIds, id])] : selectedIds.filter((s) => s !== id));
    };

    return (
        <div className="space-y-2" data-testid="attach-job-files-picker">
            <div className="flex items-center gap-1.5 text-sm font-medium">
                <Paperclip className="h-3.5 w-3.5 text-foreground/80" />
                Attach job files
            </div>
            {selectable.length > 0 ? (
                <>
                    <p className="text-xs text-foreground/60">
                        Files you check here are emailed to the customer with this send — including internal docs, so
                        check them deliberately. Nothing is attached unless you check it.
                    </p>
                    <ul className="space-y-1" aria-label="Job files available to attach">
                        {selectable.map((f) => {
                            const checked = selectedIds.includes(f.id);
                            const size = formatFileSize(f.file_size);
                            return (
                                <li key={f.id}>
                                    <label className="flex items-center gap-2 rounded-md border border-border/60 px-2.5 py-1.5 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={(e) => toggle(f.id, e.target.checked)}
                                            className="rounded border-input"
                                            aria-label={`Attach ${f.file_name}`}
                                        />
                                        <span className="min-w-0 flex-1 truncate">{f.file_name}</span>
                                        <span
                                            className={cn(
                                                "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide",
                                                INTERNAL_DOC_CATEGORIES.has(f.category)
                                                    ? "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200"
                                                    : "bg-muted text-foreground/60",
                                            )}
                                        >
                                            {categoryChipLabel(f.category)}
                                        </span>
                                        {size ? (
                                            <span className="shrink-0 text-xs text-muted-foreground">{size}</span>
                                        ) : null}
                                    </label>
                                </li>
                            );
                        })}
                    </ul>
                </>
            ) : (
                <p className="text-xs text-foreground/60">No additional files on this job to attach.</p>
            )}
            {autoRiding.length > 0 && (
                <div className="space-y-1" data-testid="auto-riding-files">
                    <p className="text-xs text-muted-foreground">
                        {onExcludedChange
                            ? "Included by the document checkboxes above — uncheck any you want left off this send:"
                            : "Already included by the document checkboxes above (not toggleable here):"}
                    </p>
                    <ul className="space-y-0.5" aria-label="Files already riding via the document checkboxes">
                        {autoRiding.map((f) => {
                            const deselectable =
                                !!onExcludedChange && DESELECTABLE_AUTO_RIDE_CATEGORIES.has(f.category);
                            const isFilingCopy = f.category === "report";
                            const excluded = excludedIds.includes(f.id);
                            return (
                                <li
                                    key={f.id}
                                    className="flex items-center gap-2 px-2.5 py-0.5 text-xs text-muted-foreground"
                                >
                                    {deselectable ? (
                                        <input
                                            type="checkbox"
                                            checked={!excluded}
                                            onChange={(e) =>
                                                onExcludedChange!(
                                                    e.target.checked
                                                        ? excludedIds.filter((x) => x !== f.id)
                                                        : [...new Set([...excludedIds, f.id])],
                                                )
                                            }
                                            className="rounded border-input"
                                            aria-label={`Include ${f.file_name} on this send`}
                                        />
                                    ) : null}
                                    <span
                                        className={cn("min-w-0 flex-1 truncate", excluded && "line-through opacity-60")}
                                    >
                                        {f.file_name}
                                    </span>
                                    <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide">
                                        {isFilingCopy
                                            ? "city filing copy — not emailed"
                                            : categoryChipLabel(f.category)}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
}
